import React from 'react';
import { toast } from 'react-toastify';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

const ToastContent = ({ title, message, type }) => {
  const getIcon = () => {
    switch (type) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'info': return <Info className="w-5 h-5 text-blue-500" />;
      default: return null;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'success': return 'border-green-500';
      case 'error': return 'border-red-500';
      case 'warning': return 'border-amber-500';
      case 'info': return 'border-blue-500';
      default: return 'border-gray-200';
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div className={`mt-0.5 p-1.5 rounded-full bg-white shadow-sm ${getBorderColor().replace('border', 'text')}`}>
        {getIcon()}
      </div>
      <div className="flex-1">
        {title && <h4 className="font-semibold text-gray-900 text-sm mb-0.5">{title}</h4>}
        <p className="text-sm text-gray-600 font-medium leading-relaxed">{message}</p>
      </div>
    </div>
  );
};

const toastConfig = {
  position: 'top-right',
  autoClose: 4000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  icon: false,
  closeButton: false,
  style: {
    padding: '16px',
    borderRadius: '16px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    minWidth: '320px',
  }
};

// Success toast
const showSuccess = (message, title = 'Success') => {
  toast.success(<ToastContent type="success" title={title} message={message} />, {
    ...toastConfig,
    className: '!bg-white/90 !backdrop-blur-md !shadow-xl !rounded-2xl !border-l-4 !border-green-500',
    style: undefined // Override default style for class based styling
  });
};

// Error toast
const showError = (message, title = 'Error') => {
  toast.error(<ToastContent type="error" title={title} message={message} />, {
    ...toastConfig,
    className: '!bg-white/90 !backdrop-blur-md !shadow-xl !rounded-2xl !border-l-4 !border-red-500',
    style: undefined
  });
};

// Warning toast
const showWarning = (message, title = 'Warning') => {
  toast.warning(<ToastContent type="warning" title={title} message={message} />, {
    ...toastConfig,
    className: '!bg-white/90 !backdrop-blur-md !shadow-xl !rounded-2xl !border-l-4 !border-amber-500',
    style: undefined
  });
};

// Info toast
const showInfo = (message, title = 'Info') => {
  toast.info(<ToastContent type="info" title={title} message={message} />, {
    ...toastConfig,
    className: '!bg-white/90 !backdrop-blur-md !shadow-xl !rounded-2xl !border-l-4 !border-blue-500',
    style: undefined
  });
};

// Confirmation toast
const showConfirm = (message, onConfirm, onCancel) => {
  toast(
    ({ closeToast }) => (
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-50 text-amber-600 rounded-full">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 text-sm mb-1">Confirmation Required</h4>
            <p className="text-sm text-gray-600 leading-relaxed max-w-xs">{message}</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => {
              if (onCancel) onCancel();
              closeToast();
            }}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              closeToast();
            }}
            className="px-3 py-1.5 text-xs font-medium bg-gray-900 text-white hover:bg-black rounded-lg transition-colors shadow-sm"
          >
            Confirm
          </button>
        </div>
      </div>
    ),
    {
      ...toastConfig,
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: true,
      className: '!bg-white/95 !backdrop-blur-md !shadow-2xl !rounded-2xl !border !border-gray-100',
      style: undefined
    }
  );
};

export { showSuccess, showError, showWarning, showInfo, showConfirm };

